// Pure, framework-free helpers for role / team-lead / demo logic.

import { isSuperAdminOwner } from "./brokerageSettings";

// True if an Admin (team lead) can currently be PICKED as team lead on a new file.
// Excludes: non-Admins, deleted (type 1), disabled (type 2),
// explicitly switched-off team leads (activeteamlead === false), and the demo account.
// A missing activeteamlead field counts as ON, so existing team leads stay selectable.
export const isBookableTeamLead = (user) => {
  if (!user) return false;
  if (user.role !== "Admin") return false;
  if (user.type === 1 || user.type === 2) return false;
  if (user.activeteamlead === false) return false;
  if (user.isDemo === true) return false;
  return true;
};

// The 10 granular permission keys a Client Care (Supervisor) user can hold.
export const SUPERVISOR_ACCESS_KEYS = [
  "approveDeal",
  "deleteDeal",
  "underContract",
  "pending",
  "closed",
  "closedVolume",
  "pendingVolume",
  "netVolume",
  "commissionVolume",
  "pendingCommission",
];

// Single source of truth for "may this person see commission figures?"
//
// Commission visibility was a role check copy-pasted across ~11 files, while
// the `commissionVolume` permission was never read by any code — so the toggle
// in User Management implied protection it didn't actually provide. Everything
// that displays a commission amount should call this instead.
//
//   - Team Lead (Admin) and Accountant: always.
//   - Client Care (Supervisor): only when granted, per person.
//   - Agents (Employee): never for other people. They still see their OWN
//     commission on their own deals; that is not gated by this.
export function canSeeCommissions(user) {
  if (!user) return false;
  if (user.type === 1 || user.type === 2) return false;
  if (user.role === "Admin" || user.role === "Accountant") return true;
  if (user.role === "Supervisor") {
    return Boolean(user.access && user.access.commissionVolume);
  }
  return false;
}

// Client Care never has commissions on screen by default, even when granted.
// They must reveal them, and that reveal resets on every login, so a permitted
// Client Care user doesn't sit with commission figures showing all day.
// Team Lead and Accountant are always-on and never see a reveal toggle.
export function requiresCommissionReveal(user) {
  return Boolean(user && user.role === "Supervisor");
}

// The roles the demo account can preview (never Super Admin).
export const DEMO_VIEW_ROLES = [
  { value: "Admin", label: "Team Lead" },
  { value: "Supervisor", label: "Client Care" },
  { value: "Employee", label: "Real Estate Agent" },
  { value: "Accountant", label: "Accountant" },
];

// True if this user is the demo / preview account.
export const isDemoUser = (user) => Boolean(user && user.isDemo === true);

// An all-true Client Care permission map, so the demo "Client Care" view hides nothing.
export const fullSupervisorAccess = () =>
  SUPERVISOR_ACCESS_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

// The "two hats" rule: does this person do Client Care DUTIES? True for
// active Client Care (Supervisor) and for active Agents with the owner-set
// "Client care duties" switch (users.isClientCare). DUTIES ONLY — never use
// this for commission visibility (canSeeCommissions above stays the one
// money rule, and it deliberately does NOT consult the flag: Henry's call
// 2026-07-31 is that flagged agents never see others' figures, which also
// means they cannot approve sale turn-ins).
export function actsAsClientCare(user) {
  if (!user) return false;
  if (user.type === 1 || user.type === 2) return false;
  if (user.role === "Supervisor") return true;
  return user.role === "Employee" && user.isClientCare === true;
}

// May this user search ALL team contacts (not just their own) when filling
// out forms? Staff who process files for other agents: the Super Admin owner,
// anyone flagged Listing/Transaction Coordinator, and Client Care.
// One place to change if eligibility ever widens.
export function canSearchTeamContacts(user) {
  if (!user) return false;
  if (user.type === 1 || user.type === 2) return false;
  if (isSuperAdminOwner(user)) return true;
  if (user.isListingCoordinator === true) return true;
  if (user.isTransactionCoordinator === true) return true;
  return actsAsClientCare(user);
}

// Returns a COPY of the demo user with its effective role overridden for preview.
// Keeps identity + isDemo intact so the "View as" switcher stays visible.
// Never writes to Firestore. superadmin is never granted here.
export const applyDemoRole = (user, role) => {
  const next = { ...user, role, isDemo: true };
  if (role === "Supervisor") {
    next.access = fullSupervisorAccess();
  }
  return next;
};
